import React, { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import useInterval from "../../../../../../hooks/tools/useInterval";
import { acceptTermsStatus } from "../../../../../../recoils/AcceptedTermsRecoil";
import { store } from "../../../../../../redux/index";
import { coreHTTPClient } from "../../../../../../services/webclient";
import PrivacyPolicies from "./PrivacyPolicies";
import Announcements from "./Announcements";
import { AnnouncementApiResponse } from "./types";

function getLoggedUserId(): number | null {
  const state = store.getState();

  if (!state.auth.logged_in) {
    return null;
  }

  const userId = state.userID.userID;

  if (!userId || userId < 1) {
    return null;
  }

  return userId;
}

function todayKey(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${now.getFullYear()}-${month}-${day}`;
}

function isNewVersionAnnouncement(
  notification: AnnouncementApiResponse
): boolean {
  const localizedTexts = [
    notification.text_pt,
    notification.text_en,
    notification.text_es,
    notification.text_ru,
    notification.text_de,
  ];

  return localizedTexts.some((text) => {
    if (!text) {
      return false;
    }

    const description = text.description ?? "";
    const secondaryButton = text.button2_text ?? text.button_text2 ?? "";

    return (
      description.includes("announcement-migration") ||
      secondaryButton.trim() !== ""
    );
  });
}

async function userHasMigratedToNewPlatform(): Promise<boolean> {
  try {
    const response = await coreHTTPClient.get("/v3/auth/profile/");
    const profile = response.data?.profile ?? response.data;

    if (!profile || typeof profile !== "object") {
      return false;
    }

    const keycloakUserId = profile.keycloak_user_id;
    const hasKeycloakUser =
      typeof keycloakUserId === "string" && keycloakUserId.trim() !== "";

    return hasKeycloakUser && profile.kc_sync_pending !== true;
  } catch {
    return false;
  }
}

async function shouldShowNewVersionModal(): Promise<boolean> {
  const userId = getLoggedUserId();

  if (userId == null) {
    return false;
  }

  if (await userHasMigratedToNewPlatform()) {
    return false;
  }

  if (localStorage.getItem(`newVersionModalSeenOn:${userId}`) === todayKey()) {
    return false;
  }

  localStorage.setItem(`newVersionModalSeenOn:${userId}`, todayKey());
  return true;
}

export default function NotificationsSystem(props) {
  const [notification, setNotification] = useState<AnnouncementApiResponse>();
  const [acceptedTerms, setAcceptedTerms] = useRecoilState(acceptTermsStatus);
  const [policyInfos, setPolicyInfos] = useState({
    is_accept_policy: false,
    accept_policy_version: null,
    accept_policy_date: null,
    last_policy_version: null,
  });

  // 5 minutes if acceptedTerms, else 5 seconds.
  const SHOW_ANNOUNCEMENT_DELAY = acceptedTerms ? 300000 : 3500;

  const updatePolicyInfos = async () => {
    try {
      const response = await coreHTTPClient.get("/v3/auth/check_policy/");
      if (response.data) {
        setPolicyInfos({
          is_accept_policy: response.data.is_accept_policy,
          accept_policy_version: response.data.accept_policy_version,
          accept_policy_date: response.data.accept_policy_date,
          last_policy_version: response.data.last_policy_version,
        });
        setAcceptedTerms(response.data.is_accept_policy);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getAnnouncement = async () => {
    try {
      updatePolicyInfos();
      let response;

      if (acceptedTerms == false) {
        response = await coreHTTPClient.get("/v3/notifications/policy/");
      } else {
        try {
          response = await coreHTTPClient.get("/v4/announcements/");
        } catch (error) {
          if (error?.response?.status !== 404) {
            throw error;
          }
          response = await coreHTTPClient.get("/v3/notifications/general/");
        }
      }

      if (response?.data?.is_active) {
        if (isNewVersionAnnouncement(response.data)) {
          if (await shouldShowNewVersionModal()) {
            setNotification(response.data);
          }
          return;
        }

        const lastAnnouncementUUID = localStorage.getItem(
          "lastAnnouncementUUID"
        );
        if (
          lastAnnouncementUUID == null ||
          lastAnnouncementUUID != response.data.uuid
        ) {
          setNotification(response.data);
        }
      }
    } catch (err) {
      console.log(err);
      setNotification(undefined);
    }
  };

  useInterval(() => {
    getAnnouncement();
  }, SHOW_ANNOUNCEMENT_DELAY);

  useEffect(() => {
    getAnnouncement();
  }, []);

  return notification ? (
    acceptedTerms == false ? (
      <PrivacyPolicies
        notification={notification}
        acceptedTerms={acceptedTerms}
        policyInfos={policyInfos}
      />
    ) : (
      <Announcements
        notification={notification}
        acceptedTerms={acceptedTerms}
        policyInfos={policyInfos}
      />
    )
  ) : null;
}
